import { AppError } from '@repo/error';
import { errorMessage, formatDate, number, statusLabel, t } from '@repo/i18n';
import { getLocale } from '@repo/i18n/browser';
import { useLocale } from '@repo/i18n/react';
import { Button } from '@repo/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@repo/ui/card';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { trpc } from '@/providers/trpc';

export const WorkflowEventsLog = ({
  reportId,
  onCompleted,
}: {
  reportId: string;
  onCompleted?: () => void;
}) => {
  useLocale();

  const utils = trpc.useUtils();
  const lastStatus = useRef<string | undefined>(undefined);
  const { data, isError } = trpc.report.getReportGeneration.useQuery(
    { reportId },
    {
      refetchInterval: (query) =>
        !query.state.data || query.state.data.status === 'PROCESSING' ? 2000 : false,
    },
  );
  const { data: report } = trpc.report.getReport.useQuery({ reportId });
  const retry = trpc.report.retryReportGeneration.useMutation({
    onSuccess: () => {
      void utils.report.getReport.invalidate({ reportId });
      void utils.report.getReportGeneration.invalidate({ reportId });
    },
    onError: (cause) => {
      const error = AppError.parse(cause);
      toast.error(errorMessage(AppError.parse(error)));
    },
  });
  useEffect(() => {
    if (data?.status && data.status !== lastStatus.current) {
      lastStatus.current = data.status;
      if (data.status !== 'PROCESSING') {
        void utils.report.getReport.invalidate({ reportId });
        onCompleted?.();
      }
    }
  }, [data?.status, onCompleted, reportId, utils]);
  if (isError) {
    return <p role="alert">{t('auth.unableToLoadResearchProgress')}</p>;
  }
  if (!data) {
    return <p className="text-muted-foreground text-sm">{t('auth.waitingForResearchProgress')}</p>;
  }
  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t('reports.generationStatus')} {statusLabel(data.status)}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-muted-foreground text-sm">
          {t('auth.resultsCoverOnlyTheWebSourcesResearchedMissingFindings')}{' '}
        </p>
        {data.progress.map((progress) => (
          <div className="space-y-1 rounded border p-3" key={progress.subjectId}>
            <p className="font-medium">
              {data.subjects.find((subject) => subject.id === progress.subjectId)?.name}
            </p>
            <p className="text-sm">
              {statusLabel(progress.stage)} · {statusLabel(progress.coverage)}
            </p>
            {progress.summary && <p className="text-sm">{progress.summary}</p>}
            {progress.budget && (
              <p className="text-xs">
                Queries: {progress.budget.queries} · Pages: {progress.budget.pages} · Active time:{' '}
                {Math.ceil(progress.budget.activeMs / 1000)}s
                {progress.budget.expanded ? ' · Expanded research' : ''}
                {progress.budget.exhausted ? ' · Budget reached' : ''}
              </p>
            )}
            <p className="text-xs">
              Successful analyses: {progress.successfulAnalyses ?? '—'} · Rejected findings:{' '}
              {progress.rejectedFindings ?? '—'}
            </p>
            {progress.categories?.map((category) => (
              <p className="text-sm" key={category.category}>
                {category.category}: {statusLabel(category.status)} — {category.explanation}
              </p>
            ))}
            {progress.platforms?.map((platform) => (
              <p className="text-xs" key={platform.platform}>
                {platform.platform}: {statusLabel(platform.status)} · {platform.profiles} profiles (
                {platform.profileStatus}) / {platform.activities} activity items (
                {platform.activityStatus}) · {platform.explanation}
              </p>
            ))}
            {progress.errors.map((message, index) => (
              <p className="text-amber-700 text-sm" key={`${index}-${message}`}>
                {message}
              </p>
            ))}
            {!!progress.diagnostics?.length && (
              <details>
                <summary className="cursor-pointer text-xs">
                  {t('reports.researchDiagnostics')}
                </summary>
                {progress.diagnostics.map((diagnostic, index) => (
                  <p className="text-xs" key={`${diagnostic.stage}-${index}`}>
                    {diagnostic.stage}: {diagnostic.code}
                    {diagnostic.statusCode ? ` · HTTP ${diagnostic.statusCode}` : ''}
                    {diagnostic.providerCode ? ` · ${diagnostic.providerCode}` : ''} ·{' '}
                    {diagnostic.attempts} attempt(s). {diagnostic.validationReason}
                  </p>
                ))}
              </details>
            )}
          </div>
        ))}
        {report?.retryReason && !report.actions.canRetry && (
          <p className="text-sm">{report.retryReason}</p>
        )}
        {report?.actions.canRetry && (
          <Button disabled={retry.isPending} onClick={() => retry.mutate({ reportId })}>
            {t('auth.retryIncompleteResearch')}{' '}
          </Button>
        )}
        <details>
          <summary className="cursor-pointer text-sm">
            {t('auth.researchedSources')}
            {number(data.sources.length)})
          </summary>
          <div className="mt-2 space-y-3">
            {data.sources.map((source) => (
              <ResearchSource key={source._id} reportId={reportId} source={source} />
            ))}
          </div>
        </details>
        <details>
          <summary className="cursor-pointer text-sm">{t('auth.researchHistory')}</summary>
          <ol className="mt-2 max-h-64 space-y-2 overflow-auto text-sm">
            {data.events.map((event) => (
              <li key={event.id}>
                {formatDate(event.at, getLocale(), {
                  hour: '2-digit',
                  minute: '2-digit',
                  second: '2-digit',
                })}{' '}
                · {event.message}
              </li>
            ))}
          </ol>
        </details>
      </CardContent>
    </Card>
  );
};

const ResearchSource = ({
  reportId,
  source,
}: {
  reportId: string;
  source: { _id: string; title: string; url: string; status: string };
}) => {
  useLocale();

  const [isOpen, setIsOpen] = useState(false);
  const { data, isLoading, isError } = trpc.report.getReportEvidence.useQuery(
    { reportId, evidenceId: source._id },
    { enabled: isOpen },
  );
  return (
    <div className="space-y-2 rounded border p-3">
      <a className="text-sm underline" href={source.url} rel="noopener noreferrer" target="_blank">
        {source.title}
      </a>
      <p className="text-muted-foreground text-xs">{source.status}</p>
      {source.status === 'read' && (
        <Button onClick={() => setIsOpen(!isOpen)} size="sm" variant="ghost">
          {isOpen ? t('auth.hideEvidence') : t('auth.inspectEvidence')}
        </Button>
      )}
      {isOpen && isLoading && <p>{t('auth.loadingEvidence')}</p>}
      {isOpen && isError && <p role="alert">{t('auth.couldNotLoadEvidence')}</p>}
      {isOpen && data && (
        <div>
          <p className="text-xs">
            {t('auth.retrieved')}{' '}
            {formatDate(data.retrievedAt, getLocale(), {
              year: 'numeric',
              month: 'short',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit',
            })}
          </p>
          <pre className="mt-2 max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs">
            {data.text}
          </pre>
        </div>
      )}
    </div>
  );
};
